/**
 * Client-side price display conversion.
 * Store prices stay in AUD (source of truth, matches actual checkout charge).
 * This only changes what browsing customers SEE based on their detected country.
 */
(function () {
  const config = window.ThemeCurrency || {};
  const BASE_CURRENCY = config.storeCurrency || 'AUD';

  // Country -> display currency. Add more entries as more countries are supported.
  const COUNTRY_CURRENCY = {
    IN: 'INR',
    AU: 'AUD',
  };

  const CURRENCY_FORMAT = {
    AUD: { locale: 'en-AU', symbol: 'A$' },
    INR: { locale: 'en-IN', symbol: '₹' },
  };

  const RATES_CACHE_KEY = 'rn_currency_rates';
  const RATES_CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours
  // Only set when a customer manually picks a currency (future switcher UI).
  // Auto-detection always re-derives from the current country, never from a stale cookie.
  const MANUAL_CURRENCY_COOKIE = 'rn_display_currency_manual';

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function detectCurrency() {
    const manual = getCookie(MANUAL_CURRENCY_COOKIE);
    if (manual && CURRENCY_FORMAT[manual]) return manual;

    const country = (config.visitorCountry || '').toUpperCase();
    
    return COUNTRY_CURRENCY[country] || BASE_CURRENCY;
  }

  async function getRates() {
    try {
      const cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.timestamp < RATES_CACHE_TTL && cached.base === BASE_CURRENCY) {
        return cached.rates;
      }
    } catch (err) {
      // ignore malformed cache
    }

    const response = await fetch(`https://open.er-api.com/v6/latest/${BASE_CURRENCY}`);
    if (!response.ok) throw new Error(`Exchange rate request failed: ${response.status}`);
    const data = await response.json();
    if (!data.rates) throw new Error('Exchange rate response missing rates');

    localStorage.setItem(
      RATES_CACHE_KEY,
      JSON.stringify({ base: BASE_CURRENCY, rates: data.rates, timestamp: Date.now() })
    );
    return data.rates;
  }

  function formatPrice(amount, currency) {
    const format = CURRENCY_FORMAT[currency];
    if (!format) return `${currency} ${amount.toFixed(2)}`;
    try {
      return new Intl.NumberFormat(format.locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (err) {
      return `${format.symbol}${amount.toFixed(2)}`;
    }
  }

  function convertElements(rate, currency) {
    const priceEls = document.querySelectorAll('[data-price-aud]');
    priceEls.forEach((el) => {
      const audAmount = parseFloat(el.getAttribute('data-price-aud'));
      if (isNaN(audAmount) || audAmount <= 0) return;

      if (!el.hasAttribute('data-original-text')) {
        el.setAttribute('data-original-text', el.textContent.trim());
      }

      if (currency === BASE_CURRENCY) {
        el.textContent = el.getAttribute('data-original-text');
        return;
      }

      el.textContent = formatPrice(audAmount * rate, currency);
    });
  }

  let currentRates = null;
  let currentCurrency = BASE_CURRENCY;

  async function applyCurrency() {
    currentCurrency = detectCurrency();

    if (currentCurrency === BASE_CURRENCY) {
      convertElements(1, BASE_CURRENCY);
      return;
    }

    try {
      currentRates = currentRates || (await getRates());
    } catch (err) {
      console.error('Currency conversion: could not fetch exchange rates, showing AUD.', err);
      return;
    }

    const rate = currentRates[currentCurrency];
    if (!rate) return;

    convertElements(rate, currentCurrency);
  }

  // Re-apply when the theme swaps in new price markup (variant change, quick-add modal, etc.)
  const observer = new MutationObserver((mutations) => {
    const hasNewPriceNodes = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.('[data-price-aud]') || node.querySelector?.('[data-price-aud]'))
      )
    );
    if (hasNewPriceNodes) applyCurrency();
  });

  document.addEventListener('DOMContentLoaded', () => {
    applyCurrency();
    observer.observe(document.body, { childList: true, subtree: true });
  });

  // Allow a future currency switcher UI to call this directly.
  window.setDisplayCurrency = function (currencyCode) {
    setCookie(MANUAL_CURRENCY_COOKIE, currencyCode, 30);
    applyCurrency();
  };
})();

console.log(window.ThemeCurrency);
console.log(window.ThemeCurrency.visitorCountry);
console.log(Shopify.country);
console.log(Shopify.currency);
document.querySelector('.price-item--regular')?.textContent